/**
 * Grasp Feedbacks popup — Partial / Did Not student comments with trainer answers.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditNoteIcon from "@mui/icons-material/EditNote";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import CodeIcon from "@mui/icons-material/Code";
import {
  fetchGraspFeedbackRows,
  respondGraspFeedback,
  type GraspFeedbackRow,
} from "./sessionAnalyticsReportApi";

type LevelFilter = "partial_did_not" | "partial" | "not_understood" | "understood";

type GraspFeedbacksDialogProps = {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  sessionName?: string;
  rows: GraspFeedbackRow[];
  onRowsChange?: (rows: GraspFeedbackRow[]) => void;
  initialLevelFilter?: LevelFilter;
  topicId?: string | null;
  topicTitle?: string | null;
  studentId?: string | null;
};

const selectStyle: React.CSSProperties = {
  height: 36,
  fontSize: 14,
  fontFamily: "inherit",
};

const headerCellStyle: React.CSSProperties = {
  backgroundColor: "#fff",
  borderBottom: "1px solid #e5e7eb",
  fontWeight: 700,
  fontSize: 12,
  padding: "10px 12px",
  color: "#4b5563",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 10,
};

const bodyCellStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderBottom: "1px solid #eef2f7",
  fontSize: 13,
  verticalAlign: "top",
  color: "#111827",
};

function rowKey(row: GraspFeedbackRow): string {
  return `${row.student_id}::${row.topic_id}`;
}

function formatClosedAt(ms: number): string {
  if (!ms || ms <= 0) return "";
  try {
    return new Date(ms).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function studentInitials(name: string): string {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#db2777", "#059669", "#d97706"];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash += name.charCodeAt(i);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function timeAgo(ms: number): string {
  if (!ms || ms <= 0) return "";
  const mins = Math.max(0, Math.floor((Date.now() - ms) / 60000));
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function isGraspRowClosed(row: GraspFeedbackRow): boolean {
  return row.status === "closed" || row.level === "understood";
}

function graspingPill(level: string, label: string) {
  if (level === "understood") {
    return {
      background: "#ecfdf5",
      color: "#047857",
      border: "1px solid #a7f3d0",
      text: label || "Understood",
    };
  }
  const partial = level === "partial";
  return {
    background: partial ? "#ffedd5" : "#fee2e2",
    color: partial ? "#c2410c" : "#dc2626",
    border: partial ? "1px solid #fed7aa" : "1px solid #fecaca",
    text: label || (partial ? "Partial" : "Did Not"),
  };
}

function confusionChipStyle(tag: string) {
  const key = tag.toLowerCase();
  if (key.includes("syntax") || key.includes("code")) {
    return { background: "#f3e8ff", color: "#7c3aed", border: "1px solid #e9d5ff" };
  }
  return { background: "#ffedd5", color: "#c2410c", border: "1px solid #fed7aa" };
}

function Chip({
  label,
  style,
}: {
  label: string;
  style: React.CSSProperties;
}) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.4,
        ...style,
      }}
    >
      {label}
    </span>
  );
}

const BLOCKED_ANSWER_TAGS = new Set([
  "IMG",
  "PICTURE",
  "VIDEO",
  "AUDIO",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "SVG",
  "CANVAS",
  "INPUT",
  "FORM",
  "SCRIPT",
  "STYLE",
  "LINK",
  "META",
  "BASE",
  "SOURCE",
  "TRACK",
  "FILE",
]);

function isBlockedUrl(value: string): boolean {
  return /^(javascript|data|blob|file|vbscript):/i.test(value.trim());
}

function hasBlockedMedia(html: string): boolean {
  return (
    /<(img|picture|video|audio|iframe|object|embed|svg|canvas|input|source|track|script)\b/i.test(
      html,
    ) || /(?:src|srcset|poster)\s*=\s*["']?(?:data|blob|file):/i.test(html)
  );
}

function sanitizeAnswerHtml(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild;
  if (!root) return "";

  const walk = (node: Element) => {
    for (const child of Array.from(node.children)) {
      if (BLOCKED_ANSWER_TAGS.has(child.tagName)) {
        child.remove();
        continue;
      }
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase();
        const value = attr.value || "";
        if (
          name.startsWith("on") ||
          name === "src" ||
          name === "srcset" ||
          name === "poster" ||
          name === "xlink:href"
        ) {
          child.removeAttribute(attr.name);
          continue;
        }
        if ((name === "href" || name === "action") && isBlockedUrl(value)) {
          child.removeAttribute(attr.name);
          continue;
        }
        if (name === "style" && /url\s*\(|expression\s*\(/i.test(value)) {
          child.removeAttribute(attr.name);
        }
      }
      walk(child);
    }
  };

  walk(root);
  return root.innerHTML;
}

function stripScripts(html: string): string {
  return sanitizeAnswerHtml(html);
}

function clipboardHasFiles(data: DataTransfer | null): boolean {
  if (!data) return false;
  if (data.files && data.files.length > 0) return true;
  return Array.from(data.items || []).some(
    (item) => item.kind === "file" || item.type.startsWith("image/"),
  );
}

function insertPlainOrSanitizedHtml(data: DataTransfer | null) {
  if (!data) return;
  const html = data.getData("text/html");
  const text = data.getData("text/plain");
  if (html) {
    document.execCommand("insertHTML", false, sanitizeAnswerHtml(html));
    return;
  }
  if (text) {
    document.execCommand("insertText", false, text);
  }
}

/** contentEditable often inserts &nbsp; for spaces — never show those as raw text. */
function normalizeNbsp(text: string): string {
  return String(text || "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/g, " ")
    .replace(/\u00a0/g, " ");
}

/**
 * Normalize trainer answer before save/display.
 * Plain contentEditable text (no real tags) becomes plain text with real spaces.
 */
function normalizeTrainerAnswer(html: string): string {
  const withSpaces = normalizeNbsp(html).replace(/<br\s*\/?>/gi, "\n");
  if (!/<[a-z][\s\S]*?>/i.test(withSpaces)) {
    return withSpaces
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+$/g, "");
  }
  return withSpaces;
}

function renderClosedTrainerAnswer(raw: string): React.ReactNode {
  const answer = normalizeTrainerAnswer(sanitizeAnswerHtml(raw));
  if (!answer) {
    return <span style={{ color: "#9ca3af" }}>—</span>;
  }
  const wrapStyle: React.CSSProperties = {
    fontSize: 13,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
  if (/<[a-z][\s\S]*?>/i.test(answer)) {
    return (
      <div
        style={wrapStyle}
        dangerouslySetInnerHTML={{ __html: stripScripts(answer) }}
      />
    );
  }
  return <span style={wrapStyle}>{answer}</span>;
}

function AnswerEditor({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (next: string) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState("");

  const commitHtml = (html: string, forceDom = false) => {
    const next = sanitizeAnswerHtml(html);
    if (ref.current && (forceDom || hasBlockedMedia(html)) && ref.current.innerHTML !== next) {
      ref.current.innerHTML = next;
    }
    onChange(next);
  };

  useEffect(() => {
    if (!ref.current) return;
    const next = sanitizeAnswerHtml(value || "");
    if (ref.current.innerHTML !== next) {
      ref.current.innerHTML = next;
    }
  }, [value]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
      return;
    }
    savedRangeRef.current = null;
  };

  const restoreSelection = () => {
    const range = savedRangeRef.current;
    ref.current?.focus();
    if (!range) return;
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  const closeLinkModal = () => {
    setLinkOpen(false);
    setLinkUrl("");
    setLinkError("");
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (!url) {
      setLinkError("Enter a link URL");
      return;
    }
    if (isBlockedUrl(url)) {
      setLinkError("This URL is not allowed");
      return;
    }
    restoreSelection();
    document.execCommand("createLink", false, url);
    commitHtml(ref.current?.innerHTML || "");
    closeLinkModal();
  };

  const run = (command: string, arg?: string) => {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(command, false, arg);
    commitHtml(ref.current?.innerHTML || "");
  };

  const tools = [
    { key: "bold", icon: <FormatBoldIcon sx={{ fontSize: 16 }} />, cmd: "bold" },
    { key: "italic", icon: <FormatItalicIcon sx={{ fontSize: 16 }} />, cmd: "italic" },
    {
      key: "underline",
      icon: <FormatUnderlinedIcon sx={{ fontSize: 16 }} />,
      cmd: "underline",
    },
    {
      key: "ul",
      icon: <FormatListBulletedIcon sx={{ fontSize: 16 }} />,
      cmd: "insertUnorderedList",
    },
    {
      key: "ol",
      icon: <FormatListNumberedIcon sx={{ fontSize: 16 }} />,
      cmd: "insertOrderedList",
    },
    { key: "link", icon: <LinkIcon sx={{ fontSize: 16 }} />, cmd: "createLink" },
    { key: "code", icon: <CodeIcon sx={{ fontSize: 16 }} />, cmd: "formatBlock" },
  ];

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        background: "#fff",
        overflow: "hidden",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <Box
        ref={ref}
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        aria-label="Write answer"
        data-placeholder="Write answer..."
        onPaste={(e) => {
          e.preventDefault();
          if (clipboardHasFiles(e.clipboardData) && !e.clipboardData?.getData("text/plain") && !e.clipboardData?.getData("text/html")) {
            return;
          }
          insertPlainOrSanitizedHtml(e.clipboardData);
          commitHtml(ref.current?.innerHTML || "", true);
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (clipboardHasFiles(e.dataTransfer)) return;
          insertPlainOrSanitizedHtml(e.dataTransfer);
          commitHtml(ref.current?.innerHTML || "");
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (clipboardHasFiles(e.dataTransfer)) {
            e.dataTransfer.dropEffect = "none";
          }
        }}
        onInput={() => commitHtml(ref.current?.innerHTML || "")}
        sx={{
          minHeight: 72,
          maxHeight: 180,
          padding: "10px 12px",
          fontSize: 13,
          lineHeight: 1.45,
          outline: "none",
          color: "#111827",
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          display: "block",
          overflowX: "hidden",
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
          "& *": {
            maxWidth: "100%",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          },
          "& pre": {
            whiteSpace: "pre-wrap",
          },
          "& img, & picture, & video, & audio, & iframe, & object, & embed, & svg, & canvas": {
            display: "none !important",
          },
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "4px 6px",
          borderTop: "1px solid #eef2f7",
          background: "#f8fafc",
        }}
      >
        {tools.map((tool) => (
          <button
            key={tool.key}
            type="button"
            disabled={disabled}
            title={tool.key}
            onMouseDown={(e) => {
              e.preventDefault();
              if (tool.cmd === "createLink") {
                saveSelection();
                setLinkUrl("");
                setLinkError("");
                setLinkOpen(true);
                return;
              }
              if (tool.cmd === "formatBlock") {
                run("formatBlock", "pre");
                return;
              }
              run(tool.cmd);
            }}
            style={{
              width: 26,
              height: 26,
              border: "none",
              background: "transparent",
              color: "#6b7280",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: disabled ? "default" : "pointer",
              borderRadius: 4,
            }}
          >
            {tool.icon}
          </button>
        ))}
      </div>
      <Dialog
        open={linkOpen}
        onClose={closeLinkModal}
        maxWidth="xs"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, pb: 1 }}>
          Insert link
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Link URL"
            placeholder="https://example.com"
            value={linkUrl}
            error={Boolean(linkError)}
            helperText={linkError || " "}
            onChange={(e) => {
              setLinkUrl(e.target.value);
              if (linkError) setLinkError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeLinkModal} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={applyLink}>
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

const GraspFeedbacksDialog: React.FC<GraspFeedbacksDialogProps> = ({
  open,
  onClose,
  sessionId,
  sessionName,
  rows,
  onRowsChange,
  initialLevelFilter = "partial_did_not",
  topicId = null,
  topicTitle = null,
  studentId = null,
}) => {
  const [levelFilter, setLevelFilter] = useState<LevelFilter>(initialLevelFilter);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState("");
  const [error, setError] = useState("");
  const [liveRows, setLiveRows] = useState<GraspFeedbackRow[]>(rows);
  const [loadingRows, setLoadingRows] = useState(false);
  const [editorOpenKeys, setEditorOpenKeys] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return;
    setLevelFilter(initialLevelFilter);
  }, [open, initialLevelFilter]);

  useEffect(() => {
    if (!open) return;
    setLiveRows([]);
  }, [open, sessionId]);

  useEffect(() => {
    if (!open) return;
    setLiveRows(rows);
  }, [open, rows, sessionId]);

  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    (async () => {
      setLoadingRows(true);
      setError("");
      try {
        const fresh = await fetchGraspFeedbackRows(sessionId);
        if (cancelled) return;
        setLiveRows(fresh);
        onRowsChange?.(fresh);
      } catch (err) {
        console.error("grasp feedbacks reload failed", err);
        if (!cancelled) {
          setLiveRows([]);
          setError("Could not load grasp feedbacks for this session.");
        }
      } finally {
        if (!cancelled) setLoadingRows(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId]);

  useEffect(() => {
    if (!open) return;
    const next: Record<string, string> = {};
    for (const row of liveRows) {
      next[rowKey(row)] = normalizeTrainerAnswer(row.trainer_answer || "");
    }
    setAnswers(next);
  }, [open, liveRows]);

  const scopedRows = useMemo(() => {
    const idNeedle = String(topicId || "").trim();
    const titleNeedle = String(topicTitle || "").trim().toLowerCase();
    return liveRows.filter((row) => {
      if (idNeedle || titleNeedle) {
        const idMatch = idNeedle && row.topic_id === idNeedle;
        const titleMatch =
          titleNeedle && row.topic_title.trim().toLowerCase() === titleNeedle;
        if (!idMatch && !titleMatch) return false;
      }
      if (studentId && row.student_id !== studentId) return false;
      return true;
    });
  }, [liveRows, topicId, topicTitle, studentId]);

  const filtered = useMemo(() => {
    return scopedRows.filter((row) => {
      if (levelFilter === "partial") return row.level === "partial";
      if (levelFilter === "not_understood") return row.level === "not_understood";
      if (levelFilter === "understood") {
        return row.level === "understood" && isGraspRowClosed(row);
      }
      return row.level === "partial" || row.level === "not_understood";
    });
  }, [scopedRows, levelFilter]);

  const applyUpdated = (key: string, updated: GraspFeedbackRow, fallback: GraspFeedbackRow) => {
    const next = liveRows.map((r) =>
      rowKey(r) === key
        ? {
            ...r,
            ...updated,
            message: updated.message || fallback.message,
            confusion_tags: updated.confusion_tags?.length
              ? updated.confusion_tags
              : fallback.confusion_tags,
            unclear_subtopics: updated.unclear_subtopics?.length
              ? updated.unclear_subtopics
              : fallback.unclear_subtopics,
            question_comment: updated.question_comment || fallback.question_comment,
          }
        : r,
    );
    setLiveRows(next);
    onRowsChange?.(next);
  };

  const handleRespond = async (row: GraspFeedbackRow, close: boolean) => {
    if (!sessionId || isGraspRowClosed(row)) return;
    const key = rowKey(row);
    setSavingKey(key);
    setError("");
    try {
      const updated = await respondGraspFeedback(sessionId, {
        student_id: row.student_id,
        topic_id: row.topic_id,
        trainer_answer: normalizeTrainerAnswer(
          sanitizeAnswerHtml(answers[key] ?? row.trainer_answer ?? ""),
        ),
        close,
      });
      if (!updated) {
        setError(close ? "Could not close this feedback." : "Could not save this answer.");
        return;
      }
      applyUpdated(key, updated, row);
    } catch (err) {
      console.error("respond grasp feedback failed", err);
      setError(close ? "Could not close this feedback." : "Could not save this answer.");
    } finally {
      setSavingKey("");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      disableEnforceFocus
      PaperProps={{ sx: { borderRadius: 1.5, minHeight: "70vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          py: 1.5,
          fontWeight: 700,
          fontSize: 18,
          color: "#111827",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          Grasp Feedbacks & Answers
          {sessionName ? (
            <Typography
              sx={{
                mt: 0.25,
                fontSize: 12,
                fontWeight: 500,
                color: "#6b7280",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {sessionName}
            </Typography>
          ) : null}
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        <style>
          {`
            [data-placeholder]:empty:before {
              content: attr(data-placeholder);
              color: #9ca3af;
            }
          `}
        </style>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="12px"
          flexWrap="wrap"
          mb={1.5}
        >
          <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap">
            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <InputLabel style={{ fontSize: 14 }}>Grasping</InputLabel>
              <Select
                value={levelFilter}
                label="Grasping"
                onChange={(e) => setLevelFilter(e.target.value as LevelFilter)}
                style={selectStyle}
              >
                <MenuItem value="partial_did_not">Partial / Did Not</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="not_understood">Did Not</MenuItem>
                <MenuItem value="understood">Understood</MenuItem>
              </Select>
            </FormControl>
            <Typography style={{ fontSize: 13, color: "#6b7280" }}>
              {filtered.length} feedback{filtered.length === 1 ? "" : "s"}
            </Typography>
          </Box>
        </Box>

        {error ? (
          <Typography color="error" sx={{ mb: 1, fontSize: 13 }}>
            {error}
          </Typography>
        ) : null}
        {loadingRows ? (
          <Typography sx={{ mb: 1, fontSize: 13, color: "#6b7280" }}>
            Loading feedbacks…
          </Typography>
        ) : null}

        <TableContainer
          component={Paper}
          elevation={0}
          style={{
            marginTop: 4,
            maxHeight: "64vh",
            overflow: "auto",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ ...headerCellStyle, width: 40 }}>#</TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 180 }}>
                  Student
                </TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 90 }}>
                  Grasping
                </TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 120 }}>
                  Topic
                </TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 160 }}>
                  Question / Comment
                </TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 140 }}>
                  Confusion Tags
                </TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 160 }}>
                  Unclear Subtopics
                </TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 240, width: 280, maxWidth: 320 }}>
                  Answer
                </TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 100 }}>
                  Status
                </TableCell>
                <TableCell style={{ ...headerCellStyle, minWidth: 150 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    align="center"
                    style={{ ...bodyCellStyle, padding: "24px 12px", color: "#6b7280" }}
                  >
                    No feedback for this filter.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row, index) => {
                  const key = rowKey(row);
                  const closed = isGraspRowClosed(row);
                  const pill = graspingPill(row.level, row.level_label);
                  const message = String(row.message || row.question_comment || "").trim();
                  const tags = row.confusion_tags || [];
                  const unclear = row.unclear_subtopics || [];
                  const editorOpen = Boolean(editorOpenKeys[key]);
                  const highlight = index === 0 && !closed;
                  return (
                    <TableRow
                      key={key}
                      hover
                      style={{
                        background: highlight ? "#f0f7ff" : "#fff",
                        boxShadow: highlight ? "inset 3px 0 0 #2563eb" : undefined,
                      }}
                    >
                      <TableCell style={bodyCellStyle}>{index + 1}</TableCell>
                      <TableCell style={bodyCellStyle}>
                        <Box sx={{ display: "flex", gap: 1.25, minWidth: 0 }}>
                          <span
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              background: avatarColor(row.student_name),
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 700,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {studentInitials(row.student_name)}
                          </span>
                          <Box sx={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: "#111827" }}>
                              {row.student_name}
                            </div>
                            {row.submitted_at ? (
                              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                                {timeAgo(row.submitted_at)}
                              </div>
                            ) : null}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        <Chip
                          label={pill.text}
                          style={{
                            background: pill.background,
                            color: pill.color,
                            border: pill.border,
                            borderRadius: 999,
                          }}
                        />
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {row.topic_title || "—"}
                      </TableCell>
                      <TableCell style={{ ...bodyCellStyle, whiteSpace: "normal" }}>
                        {message ? (
                          <Box>
                            <div
                              style={{
                                fontSize: 11,
                                color: "#6b7280",
                                marginBottom: 4,
                              }}
                            >
                              Message
                            </div>
                            <div
                              style={{
                                fontWeight: 700,
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {message}
                            </div>
                          </Box>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        )}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {tags.length ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                            {tags.map((tag) => (
                              <Chip key={tag} label={tag} style={confusionChipStyle(tag)} />
                            ))}
                          </Box>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        )}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {unclear.length ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                            {unclear.map((item) => (
                              <Chip
                                key={item}
                                label={item}
                                style={{
                                  background: "#dbeafe",
                                  color: "#1d4ed8",
                                  border: "1px solid #bfdbfe",
                                }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        )}
                      </TableCell>
                      <TableCell
                        style={{
                          ...bodyCellStyle,
                          whiteSpace: "normal",
                          width: 280,
                          maxWidth: 320,
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                        }}
                      >
                        {closed ? (
                          renderClosedTrainerAnswer(row.trainer_answer || "")
                        ) : editorOpen ? (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 220 }}>
                            <AnswerEditor
                              value={answers[key] ?? ""}
                              disabled={savingKey === key}
                              onChange={(next) =>
                                setAnswers((prev) => ({ ...prev, [key]: next }))
                              }
                            />
                            <button
                              type="button"
                              disabled={savingKey === key}
                              onClick={() => {
                                setEditorOpenKeys((prev) => ({
                                  ...prev,
                                  [key]: false,
                                }));
                                setAnswers((prev) => ({
                                  ...prev,
                                  [key]: normalizeTrainerAnswer(
                                    row.trainer_answer || "",
                                  ),
                                }));
                              }}
                              style={{
                                alignSelf: "flex-start",
                                height: 28,
                                padding: "0 10px",
                                border: "1px solid #d1d5db",
                                borderRadius: 6,
                                background: "#fff",
                                color: "#4b5563",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: savingKey === key ? "default" : "pointer",
                              }}
                            >
                              Cancel
                            </button>
                          </Box>
                        ) : (
                          <button
                            type="button"
                            title="Write answer"
                            aria-label="Write answer"
                            onClick={() =>
                              setEditorOpenKeys((prev) => ({
                                ...prev,
                                [key]: true,
                              }))
                            }
                            style={{
                              width: 36,
                              height: 36,
                              border: "1px solid #dbeafe",
                              borderRadius: 8,
                              background: "#eff6ff",
                              color: "#2563eb",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                            }}
                          >
                            <EditNoteIcon sx={{ fontSize: 20 }} />
                          </button>
                        )}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {closed ? (
                          <Chip
                            label="Closed"
                            style={{
                              background: "#ecfdf5",
                              color: "#047857",
                              border: "1px solid #a7f3d0",
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "3px 8px",
                              borderRadius: 999,
                              background: "#ffedd5",
                              color: "#c2410c",
                              border: "1px solid #fed7aa",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 14 }} />
                            Pending
                          </span>
                        )}
                        {closed && row.closed_at > 0 ? (
                          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
                            {formatClosedAt(row.closed_at)}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell style={bodyCellStyle}>
                        {closed ? (
                          <span style={{ fontSize: 12, color: "#6b7280" }}>—</span>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={savingKey === key}
                            onClick={() => void handleRespond(row, true)}
                            style={{
                              height: 32,
                              fontSize: 12,
                              textTransform: "none",
                              border: "1px solid #d1d5db",
                              color: "#111827",
                              fontWeight: 600,
                            }}
                          >
                            {savingKey === key ? "Closing…" : "Close"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default GraspFeedbacksDialog;
